import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { DateTime } from 'luxon'

import Component from 'components/component'
import ShareView from 'components/share/view'
import ViewCollapse from 'components/view-collapse'
import { videosFilter, landing } from 'config/routes'
import { DI } from 'modules'
import { linksWrapping } from 'utils/links-wrapping'
import MetaData from './meta-data'
import { IS_LANDING } from 'config'
import { hasDST } from 'utils/check-dst-date'

const openScriptDate = DateTime.local(2019, 8, 12, 0, 0)

function Description({
  entity = {},
  common = {},
  isMobile = false,
  user,
  actions = {},
  getImgUrl = () => {},
}) {
  const string = key => common.strings[`page.video.${key}`]

  const offset = DateTime.fromISO(entity.published_at).offset
  const isInDST = DateTime.fromISO(entity.published_at).isInDST // is summer time
  const fixedZone = !hasDST(entity.published_at)
    ? offset
    : isInDST
      ? offset
      : offset + 60

  const publishedAt = DateTime.fromISO(entity.published_at)
    .setZone(fixedZone)
    .toFormat(string('date.format'))

  const signIn = useCallback(
    ev => {
      ev.preventDefault()
      console.log(actions)
      actions.common.openSignInModal()
    },
    [actions]
  )

  const showMetadata = user.isSignedIn && entity
  const showShots = user.isSignedIn && entity.shots && entity.shots.length

  const shortsList = useMemo(() => {
    return showShots
      ? entity.shots.split('\n').map((el, index) => <p key={index}>{el}</p>)
      : null
  }, [entity.shots, showShots])

  let parsedDescription = ''

  if (entity.description) {
    parsedDescription = entity.description.split(/\n/g).map((text, index) => (
      <p key={index}>
        <TextWithLinks text={text} />
      </p>
    ))
  }

  if (isMobile) {
    return (
      <div className="live-description-mobile">
        <span className="live-description-mobile__title">
          {string('panel.desc')}
        </span>
        {parsedDescription && (
          <LiveDesc
            signedIn={user.isSignedIn}
            publishedAt={entity.published_at}
            description={parsedDescription}
            strings={common.strings}
            isMobile
            onSignIn={signIn}
          />
        )}

        {showShots && (
          <div className="live-targets">
            <ViewCollapse title={string('right.shot_list')}>
              <div className="live-target__list">{shortsList}</div>
            </ViewCollapse>
          </div>
        )}
        {showMetadata && <MetaData entity={entity} />}
      </div>
    )
  }

  function renderLocationName() {
    // if (entity.region_name) return [entity.region_name, entity.country_name].join(', ')
    return [entity.country_name, entity.location].filter(Boolean).join(', ')
  }

  return (
    <div className="live-description">
      <div className="live-description__head">
        <ShareView
          titleOfStr={'page.lives.view.share'}
          entity={{
            id: entity.id,
            title: entity.title,
            desc: entity.description,
            img: getImgUrl('medium'),
          }}
          alignLeft
        />
      </div>
      <h1 className="live__title">{entity.title}</h1>
      <div className="live-place">
        <span className="live__place">{renderLocationName()}</span>
      </div>
      {!IS_LANDING && <Tags tags={entity.tags} locale={common.locale} />}
      <div className="live-update">
        {`${publishedAt} · ${string('properties.published')} `}
      </div>
      {parsedDescription && (
        <LiveDesc
          signedIn={user.isSignedIn}
          strings={common.strings}
          publishedAt={entity.published_at}
          description={parsedDescription}
          onSignIn={signIn}
        />
      )}
      {showShots && (
        <div className="live-targets _desktop">
          <ViewCollapse title={string('right.shot_list')}>
            <div className="live-target__list">{shortsList}</div>
          </ViewCollapse>
        </div>
      )}
      {showMetadata && <MetaData entity={entity} isDesktop />}
    </div>
  )
}

function LiveDesc({
  signedIn,
  strings,
  publishedAt,
  isMobile,
  description,
  onSignIn,
}) {
  const descEl = useRef(null)
  const [maxHeight, setMaxHeight] = useState(null)

  useEffect(() => {
    // if (descEl.current) {
    if (descEl.current && DateTime.fromISO(publishedAt) >= openScriptDate) {
      setMaxHeight(descEl.current.getBoundingClientRect().height * 0.7)
    }
  }, [])

  return [
    <div
      key="description"
      style={{ maxHeight: signedIn ? void 0 : maxHeight }}
      className={Component.classList(
        isMobile ? 'live-description-mobile-text' : 'live-desc',
        !signedIn && '_not-auth'
      )}
    >
      <div ref={descEl}>{description}</div>
    </div>,
    signedIn || (
      <div
        key="overlay"
        className={Component.classList(
          'live-sign-up-overlay',
          !isMobile && '_desktop'
        )}
      >
        {IS_LANDING && (
          <>
            <button className="text-button" onClick={onSignIn}>
              {strings['page.video.descrtiption.login']}
            </button>
            {strings['page.video.descrtiption.login.text']}
          </>
        )}
      </div>
    ),
  ]
}

function Tags({ tags = [], locale }) {
  if (!tags.length) return null

  return (
    <div className="live-tags">
      {tags.map(tag => {
        if (tag.name) {
          return (
            <Link
              key={tag.id}
              to={
                tag.view_type === 'landing'
                  ? landing.getPath(locale, tag.slug)
                  : videosFilter.getPath(
                      locale,
                      tag.state === 'regular' ? 'tag' : 'category',
                      `${tag.id}-${tag.name}`
                    )
              }
              className={'live__tag'}
            >
              {String(tag.name)}
            </Link>
          )
        }
      })}
    </div>
  )
}

function TextWithLinks({ text }) {
  const config = [
    {
      reg: /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi,
    },
    {
      reg: /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi,
      type: 'mailto:',
    },
  ]
  return linksWrapping(text, config)
}

export default DI(['common', 'user'])(Description)
